import {
  Dispatch,
  SetStateAction,
  ChangeEvent,
  useCallback,
  useState,
  useEffect,
} from "react";
import {
  AppBar as AppBarMui,
  Box,
  FormControl,
  IconButton,
  useTheme,
  styled,
  Toolbar as ToolbarMui,
  InputBase as InputBaseMui,
  Paper as PaperMui,
  alpha,
  ClickAwayListener,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import {
  scrollToTop,
  NavigateBeforeButton,
  SearchSuggestions,
  SearchBarContainer as SearchBarContainerComponent,
} from "@components";
import { Suggestion } from "@interfaces/index";

const SearchBarContainer = styled(SearchBarContainerComponent)(() => ({
  flex: 1,
}));

const PreliminarySearchResults = styled(SearchSuggestions)(
  ({ theme, focused }) => ({
    [theme.breakpoints.down("md")]: {
      display: focused ? "flex" : "none",
      width: "100vw",
      position: "fixed",
      top: 64,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1000,
      height: "fit-content",
      boxShadow: theme.shadows[2],
    },
  })
);

const AppBar = styled(AppBarMui)({
  display: "flex",
  justifyContent: "center",
});

const LeftContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 12,
  justifyContent: "flex-start",
  alignItems: "center",
  flex: 1,
  [theme.breakpoints.down("md")]: {
    flex: 0,
  },
}));

const RightContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 12,
  justifyContent: "flex-end",
  alignItems: "center",
  flex: 1,
  [theme.breakpoints.down("md")]: {
    flex: 0,
  },
}));

const Toolbar = styled(ToolbarMui)(({ theme }) => ({
  paddingLeft: 12,
  paddingRight: 12,
  gap: 6,
  backgroundColor: theme.palette.background.paper,
  minHeight: 64,
}));

const PaperForm = styled(PaperMui)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: 20,
  overflow: "hidden",
  width: "100%",
  maxWidth: "620px",
  position: "absolute",
  [theme.breakpoints.up("md")]: {
    width: "620px",
  },
  border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
  "&:focus-within": {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  "&:focus": {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  "&:active": {
    border: `2px solid ${theme.palette.primary.main}`,
  },
}));

const InputBase = styled(InputBaseMui)(({ theme }) => ({
  height: "40px",
  fontSize: "16px",
  marginRight: "8px",
  marginLeft: "8px",
  paddingRight: 0,
  "& input": {
    height: "35px",
    padding: "9px",
  },
  color: theme.palette.text.gray,
}));

interface Props {
  search?: () => Promise<void>;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
  handleBack: () => void;
  placeholder: string;
  preliminaryResults: Suggestion[];
  onPreliminaryItemClick: (item: Suggestion) => void;
  locationState: any;
  addManuallyItemText?: string;
  autoFocus?: boolean;
  elevation?: number;
  showAddManuallyItemButton?: boolean;
  showEmptyResultsMessage?: boolean;
  showErrorResultsMessage?: boolean;
  showLoader?: boolean;
}

export function SearchHeader({
  searchText,
  search = () => Promise.resolve(),
  setSearchText,
  handleBack,
  placeholder,
  preliminaryResults,
  onPreliminaryItemClick,
  locationState,
  addManuallyItemText,
  autoFocus,
  elevation,
  showAddManuallyItemButton,
  showEmptyResultsMessage,
  showErrorResultsMessage,
  showLoader,
}: Props) {
  const theme = useTheme();

  const [focused, setFocused] = useState<boolean>(false);

  const handleChange = useCallback(
    (setter: Dispatch<SetStateAction<string>>) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        setter(event.target.value);
      },
    []
  );

  const handleEmptySearch = useCallback(() => {
    setSearchText("");
    scrollToTop();
  }, [setSearchText]);

  const handleDelete = useCallback(() => {
    setSearchText("");
    scrollToTop();
  }, [setSearchText]);

  useEffect(() => {
    if (search) {
      void search();
    }
  }, [search]);

  return (
    <ClickAwayListener
      onClickAway={() => {
        setFocused(false);
      }}
    >
      <AppBar position="sticky" color="transparent" elevation={elevation}>
        <Toolbar disableGutters>
          <LeftContainer>
            <NavigateBeforeButton onClick={handleBack} />
          </LeftContainer>

          <SearchBarContainer>
            <PaperForm elevation={0}>
              <FormControl fullWidth>
                <InputBase
                  autoFocus={autoFocus}
                  value={searchText}
                  onChange={handleChange(setSearchText)}
                  onFocus={() => {
                    setFocused(true);
                  }}
                  endAdornment={
                    searchText ? (
                      <IconButton onClick={() => handleDelete()}>
                        <Close style={{ color: theme.palette.text.black }} />
                      </IconButton>
                    ) : (
                      <IconButton onClick={() => handleEmptySearch()}>
                        <Search style={{ color: theme.palette.text.black }} />
                      </IconButton>
                    )
                  }
                  placeholder={placeholder}
                />
                <PreliminarySearchResults
                  focused={focused}
                  setFocused={setFocused}
                  searchText={searchText}
                  preliminarySearchResults={preliminaryResults}
                  onItemClick={onPreliminaryItemClick}
                  locationState={locationState}
                  addManuallyItemText={addManuallyItemText}
                  showAddManuallyItemButton={showAddManuallyItemButton}
                  showEmptyResultsMessage={showEmptyResultsMessage}
                  showErrorResultsMessage={showErrorResultsMessage}
                  showLoader={showLoader}
                />
              </FormControl>
            </PaperForm>
          </SearchBarContainer>
          <RightContainer></RightContainer>
        </Toolbar>
      </AppBar>
    </ClickAwayListener>
  );
}
